import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FileModel } from '../../../core/models/file.model';
import { FilesService } from '../../../core/services/files.service';
import { isPreviewableImage } from '../../../core/utils/image-file';
@Component({
  selector: 'app-file-image-preview',
  imports: [FontAwesomeModule],
  templateUrl: './file-image-preview.component.html',
  styleUrl: './file-image-preview.component.css'
})
export class FileImagePreviewComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true })
  file!: FileModel;
  @Input()
  showThumbnail = false;
  @Input()
  primaryButton = false;
  @ViewChild('imageDialog')
  previewDialog!: ElementRef<HTMLDialogElement>;
  readonly isImage = isPreviewableImage;
  readonly previewIcon = faEye;
  thumbnailUrl = '';
  fullImageUrl = '';
  imageError = false;
  thumbnailLoading = false;
  fullImageError = false;
  private thumbnailRequest?: Subscription;
  private fullImageRequest?: Subscription;
  private observer?: IntersectionObserver;
  private currentFileKey = '';
  constructor(private files: FilesService, private element: ElementRef<HTMLElement>) { }
  ngAfterViewInit(): void {
    this.observeThumbnailVisibility();
  }
  ngOnChanges(): void {
    const key = `${this.file.id}|${this.file.contentType}|${this.showThumbnail}`;
    if (key === this.currentFileKey)
      return;
    this.currentFileKey = key;
    this.releaseImages();
    this.imageError = false;
    this.observeThumbnailVisibility();
  }
  private observeThumbnailVisibility(): void {
    this.observer?.disconnect();
    if (!this.showThumbnail || !this.isImage(this.file))
      return;
    this.observer ??= new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.loadThumbnail();
        this.observer?.disconnect();
      }
    });
    this.observer.observe(this.element.nativeElement);
  }
  loadThumbnail(): void {
    if (!this.showThumbnail || !this.isImage(this.file) || this.thumbnailUrl || (this.thumbnailRequest && !this.thumbnailRequest.closed))
      return;
    this.imageError = false;
    this.thumbnailLoading = true;
    this.thumbnailRequest = this.files.thumbnailImage(this.file.id).subscribe({
      next: blob => { this.thumbnailUrl = URL.createObjectURL(blob); },
      error: () => this.onThumbnailError()
    });
  }
  onThumbnailError(): void {
    this.imageError = true;
    this.thumbnailLoading = false;
    this.thumbnailRequest = undefined;
    if (this.thumbnailUrl)
      URL.revokeObjectURL(this.thumbnailUrl);
    this.thumbnailUrl = '';
  }
  openPreview(event: Event): void {
    event.stopPropagation();
    if (!this.previewDialog.nativeElement.open)
      this.previewDialog.nativeElement.showModal();
    if (this.fullImageUrl)
      return;
    this.fullImageError = false;
    this.fullImageRequest?.unsubscribe();
    this.fullImageRequest = this.files.previewImage(this.file.id).subscribe({
      next: blob => { this.fullImageUrl = URL.createObjectURL(blob); },
      error: () => { this.fullImageError = true; }
    });
  }
  closeOnBackdrop(event: MouseEvent): void {
    event.stopPropagation();
    if (event.target === this.previewDialog.nativeElement)
      this.previewDialog.nativeElement.close();
  }
  private releaseImages(): void {
    this.thumbnailRequest?.unsubscribe();
    this.fullImageRequest?.unsubscribe();
    this.thumbnailRequest = undefined;
    this.fullImageRequest = undefined;
    this.previewDialog?.nativeElement.close();
    if (this.thumbnailUrl)
      URL.revokeObjectURL(this.thumbnailUrl);
    if (this.fullImageUrl)
      URL.revokeObjectURL(this.fullImageUrl);
    this.thumbnailUrl = '';
    this.thumbnailLoading = false;
    this.fullImageUrl = '';
  }
  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.releaseImages();
  }
}
